import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../components/custom-table/custom-table";
import SftpDocModal from "../../../components/modals/sftpDocModal/sftpDocModal";
import NycbsExternalLogsModal from "../../../components/modals/nycbsExternalLogsModal/nycbsExternalLogsModal";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { CmmStatus } from "../../../enums/cmmStatus";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { useModalOpener } from "../../../hooks/useModalOpener";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { useNycbsExternalLogsTrail } from "../hooks/useNycbsExternalLogsTrail";
import { useNycbsExternalTableData } from "../hooks/useNycbsExternalTableData";
import {
  generateTableDataForNycbsExternalTable,
  NycbsExternalTableHeader,
} from "../table/nycbsExternalTableData";
import { isNycbsSummaryViewDisabled } from "../table/utils/isNycbsSummaryViewDisabled";
import { buildNycbsExternalLogsModalData } from "../utils/buildNycbsExternalLogsModalData";
import { createAllFilterSections } from "../utils/createFilterSections";

interface NycbsExternalTableProps {
  /** BigQuery org id — Non-STAT (`NYCBS_PHARMA`) or STAT (`NYCBS_STATS`). */
  orgId: string;
}

const NycbsExternalTable = ({ orgId }: NycbsExternalTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();

  useModalOpener();

  const getLast14DaysDateRange = () => {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const endDate = `${year}${month}${day}`;
    const startDateObj = new Date(now);
    startDateObj.setDate(now.getDate() - 13);
    const startYear = startDateObj.getFullYear();
    const startMonth = String(startDateObj.getMonth() + 1).padStart(2, "0");
    const startDay = String(startDateObj.getDate()).padStart(2, "0");
    const startDate = `${startYear}${startMonth}${startDay}`;

    return { startDate, endDate };
  };

  const { startDate, endDate } = getLast14DaysDateRange();
  const pageSize = 120;

  const [filterData, setFilterData] = useState<FilterSection[]>([]);
  const [allDataForFilters, setAllDataForFilters] = useState<any[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { nycbsExternalState, setNycbsExternalState, tableData, setTableData } =
    useNycbsExternalTableData();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setNycbsExternalState,
      setTableData,
      startDate,
      endDate,
      pageSize,
      orgId,
    );

  const { paginationResetCount, setSearchTerm, searchTerm, validFilters } =
    useFilterAndSearch(
      initialDataFetchingExecuted,
      setTableData,
      setNycbsExternalState,
      startDate,
      endDate,
      pageSize,
      orgId,
    );

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    nycbsExternalState,
    setNycbsExternalState,
    setTableData,
    startDate,
    endDate,
    pageSize,
    orgId,
  );

  const [allNycbsExternalOrdersTableData, setAllStpFileOrdersTableData] =
    useState<any[]>([]);
  const [selectedLogsOrder, setSelectedLogsOrder] =
    useState<PharmaStpFileModel | null>(null);

  const {
    isLoading: isLogsTrailLoading,
    rows: logsTrailRows,
    loadTrail: loadLogsTrail,
    reset: resetLogsTrail,
  } = useNycbsExternalLogsTrail(orgId);

  const selectedLogsOrderData = useMemo(
    () =>
      selectedLogsOrder
        ? buildNycbsExternalLogsModalData(selectedLogsOrder, logsTrailRows)
        : null,
    [selectedLogsOrder, logsTrailRows],
  );

  useEffect(() => {
    const formattedTableData = generateTableDataForNycbsExternalTable(tableData);
    setAllStpFileOrdersTableData(formattedTableData);

    if (
      tableData &&
      tableData.length > 0 &&
      (isFirstLoad || !validFilters || validFilters.length === 0)
    ) {
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }

      setAllDataForFilters((prevData) => {
        const combinedData = [...prevData, ...tableData];

        const uniqueData = combinedData.reduce((acc, item) => {
          if (!acc.some((x: any) => x.identifier === item.identifier)) {
            acc.push(item);
          }
          return acc;
        }, [] as any[]);

        return uniqueData;
      });
    }
  }, [tableData, validFilters, isFirstLoad]);

  const handleSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  const handleRefresh = () => {
    setInitialDataFetchingExecuted(false);
    setAllDataForFilters([]);
    setIsFirstLoad(true);
  };

  const handleViewCmmClick = (rowData: any) => {
    const responseStatus =
      rowData.response_status ?? rowData.responseStatusValue ?? null;
    const cmmStatus = rowData.cmmStatus ?? null;

    if (isNycbsSummaryViewDisabled(responseStatus, cmmStatus)) {
      return;
    }

    if (cmmStatus === CmmStatus.COMPLETED) {
      dispatch(
        setOpenedModalId({
          id: ModalId.SFTP_DOC_MODAL,
          metaData: {
            selectedRowData: rowData,
          },
        }),
      );
    }
  };

  const handleLogClick = (rowData: any) => {
    const order: PharmaStpFileModel =
      tableData.find(
        (item: PharmaStpFileModel) => item.identifier === rowData.identifier,
      ) ?? rowData;

    // Open on the order fields we already have, then fill in the recorded
    // pushes when they arrive — the modal header needs no round trip.
    setSelectedLogsOrder(order);
    dispatch(setOpenedModalId({ id: ModalId.NYCBS_EXTERNAL_LOGS_MODAL }));

    void loadLogsTrail({
      inputFilename: order.filename ?? "",
      caseSeq: order.seq ?? order.sequence ?? null,
      identifier: order.identifier ?? null,
    });
  };

  const handleCloseLogsModal = () => {
    dispatch(closeModal());
    setSelectedLogsOrder(null);
    resetLogsTrail();
  };

  const handleCloseSftpDocModal = () => {
    dispatch(closeModal());
  };

  useEffect(() => {
    if (allDataForFilters && allDataForFilters.length > 0) {
      const filters = createAllFilterSections(allDataForFilters);
      setFilterData(filters);
    }
  }, [allDataForFilters]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  const tableContextData = getTableDataFromContext(
    TableNames.NYCBS_EXTERNAL_ORDERS,
  );

  useEffect(() => {
    if (tableContextData?.shouldRefetch) {
      setInitialDataFetchingExecuted(false);
      setTableDataForContext(TableNames.NYCBS_EXTERNAL_ORDERS, {
        shouldRefetch: false,
      });
    }
  }, [
    tableContextData?.shouldRefetch,
    setInitialDataFetchingExecuted,
    setTableDataForContext,
  ]);

  if (nycbsExternalState.error) {
    console.error("Error fetching STP file data:", nycbsExternalState.error);
  }

  return (
    <>
      <CustomTable
        tableHeaders={NycbsExternalTableHeader}
        tableData={allNycbsExternalOrdersTableData}
        itemsPerPage={10}
        pagesPerView={4}
        count={allNycbsExternalOrdersTableData.length}
        totalCount={nycbsExternalState.data?.total_count ?? 0}
        isFetching={nycbsExternalState.loading}
        tableName={TableNames.NYCBS_EXTERNAL_ORDERS}
        searchingText={handleSearch}
        placeholder="Search by Seq, MRN, Patient Name, Drug"
        onViewCmmClick={(data) => handleViewCmmClick(data)}
        onTextNoteClick={(data) => handleLogClick(data)}
        onRefreshButton={handleRefresh}
        isDisabled={nycbsExternalState.loading}
        showRefreshButton={true}
        showFilterButton={true}
        filterSectionsData={filterData}
        onFilterApplyClicked={handleFilterApply}
        endIndexOfTable={setEndIndex}
        requestForPaginationReset={paginationResetCount}
        showInLineLoader={nycbsExternalState.showInlineLoader}
      />
      {openedModalId === ModalId.SFTP_DOC_MODAL && (
        <SftpDocModal
          onClose={handleCloseSftpDocModal}
          showCallingSummaryOnlyWhenCallRequired
        />
      )}
      {openedModalId === ModalId.NYCBS_EXTERNAL_LOGS_MODAL &&
        selectedLogsOrderData && (
          <NycbsExternalLogsModal
            onClose={handleCloseLogsModal}
            orderData={selectedLogsOrderData}
            isLoading={isLogsTrailLoading}
          />
        )}
    </>
  );
};

export default NycbsExternalTable;
